import React, { useEffect, useState } from 'react';
import { inversionApi } from '../lib/api';

/** /map link that opens the Inversion panel on a stored model of this dataset. */
export function openOnMapPath({ projectId, datasetId, modelId }) {
  const q = new URLSearchParams();
  if (projectId) q.set('project', projectId);
  if (datasetId) q.set('dataset', datasetId);
  q.set('model', modelId);
  return `/map?${q.toString()}`;
}

const iterationsText = (m) => {
  const n = m.n_iterations ?? (m.iterations || []).length;
  const parts = [];
  if (n) parts.push(`${m.iteration != null ? `iteration ${m.iteration} of ${n}` : `${n} iterations`}`);
  if (m.rms_percent != null) parts.push(`RMS ${Number(m.rms_percent).toFixed(2)}%`);
  return parts.join(' · ') || '—';
};

/**
 * Dataset page card (V.12): inversion models stored with this dataset (inversion_models.dataset_id,
 * set when a model is imported from the map's Inversion panel opened on this dataset).
 * Hidden when there are none unless `showEmpty` (resistivity-like datasets).
 *
 *   dataset   - { id, project_id }
 *   onOpen(path) - navigate to the map
 */
export default function InversionResultsCard({ dataset, onOpen, showEmpty = false }) {
  const [models, setModels] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const datasetId = dataset?.id;

  useEffect(() => {
    if (!datasetId) return undefined;
    let live = true;
    inversionApi.list({ dataset_id: datasetId })
      .then((res) => { if (live) { setModels(res.data || []); setError(null); } })
      .catch((err) => { if (live) { setModels([]); setError(err?.response?.data?.detail || 'Could not load inversion results'); } });
    return () => { live = false; };
  }, [datasetId]);

  if (models === null) return null;
  if (models.length === 0 && !error && !showEmpty) return null;

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="inversion-results-card">
      <h2 className="text-lg font-semibold mb-4">Inversion results</h2>
      {error && <p className="text-sm text-destructive mb-2">{error}</p>}
      {models.length === 0 && !error && (
        <p className="text-sm text-muted-foreground">
          No inversion results linked yet. Open this dataset on the map and import a result in the Inversion panel; it is stored with this dataset.
        </p>
      )}
      {models.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-2 font-medium">Name</th>
              <th className="py-1 pr-2 font-medium">Type</th>
              <th className="py-1 pr-2 font-medium">Iterations / RMS</th>
              <th className="py-1 pr-2 font-medium">Georeferenced</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {models.map((m) => (
              <tr key={m.id} className="border-t border-border" data-testid="inversion-result-row">
                <td className="py-1.5 pr-2">{m.name}</td>
                <td className="py-1.5 pr-2">{m.format || '—'}{m.geometry_kind ? ` (${m.geometry_kind})` : ''}</td>
                <td className="py-1.5 pr-2 monospace">{iterationsText(m)}</td>
                <td className="py-1.5 pr-2">{m.georeferenced ? 'Yes' : 'No'}</td>
                <td className="py-1.5 text-right">
                  <button type="button" className="text-primary hover:underline"
                    onClick={() => onOpen(openOnMapPath({ projectId: m.project_id || dataset.project_id, datasetId, modelId: m.id }))}>
                    Open on map
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
